import React, { useEffect, useState, useRef } from 'react'
import { useSessionCleanup, useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useTranslation } from 'react-i18next'
import { useCountdown } from '@renderer/utils/countdownUtils'
import QRCode from 'qrcode'
import { FaStar } from 'react-icons/fa'
import { IoMail } from 'react-icons/io5'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import ConsentModal from '@renderer/components/ConsentModal'
import UserReprintModal from '@renderer/components/UserReprintModal'
import EmailGalleryModal from '@renderer/components/EmailGalleryModal'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { getGalleryBaseUrl } from '@renderer/utils/brandUtils'
import { resolveGallerySessionUrl } from '@renderer/utils/galleryUrlUtils'

const ResultPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const {
    gallery,
    id,
    timeline,
    setSessionState,
    logActivity,
    userPreferences
  } = useSessionStateStore()
  const sessionCleanup = useSessionCleanup()
  const { config: globalConfig } = useConfigStore()
  const config = globalConfig?.pages?.result

  const [stripPreview, setStripPreview] = useState<string>('')
  const [qrCodeImage, setQrCodeImage] = useState<string>('')
  const [remainingTime, setRemainingTime] = useState<string>('--:--')
  const [satisfactionRating, setSatisfactionRating] = useState<number | null>(
    userPreferences.satisfactionRating ?? null
  )
  const [hoveredStar, setHoveredStar] = useState<number | null>(null)

  const askUserConsent =
    config?.settings?.askUserConsent === undefined ? true : config?.settings?.askUserConsent
  const [showConsent, setShowConsent] = useState(askUserConsent)
  const [showReprint, setShowReprint] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)

  const hasCompleted = useRef(false)
  const isMountedRef = useRef(true)

  const resultCountdown = useCountdown(config?.settings?.resultTimeout || 120)

  const handleConsent = async (consent: boolean): Promise<void> => {
    const reprintEnabled = config?.settings?.reprintEnabled ?? false

    if (reprintEnabled) {
      setShowReprint(true)
    }

    setShowConsent(false)

    setSessionState({
      userConsent: {
        photoPublication: consent,
        consentTimestamp: new Date()
      }
    })

    logActivity(`User ${consent ? 'granted' : 'declined'} photo publication consent`, {
      consent,
      timestamp: new Date()
    })

    await new Promise((resolve) => setTimeout(resolve, 100))
    const currentState = useSessionStateStore.getState()

    if (currentState.userConsent?.photoPublication !== consent) {
      console.warn('[CONSENT] Consent value mismatch, retrying state update')
      setSessionState({
        userConsent: {
          photoPublication: consent,
          consentTimestamp: new Date()
        }
      })
      await new Promise((resolve) => setTimeout(resolve, 100))
    }

    await stateUploader.uploadSessionState(id)
    console.log('[CONSENT] Uploaded consent:', consent)

    if (reprintEnabled) {
      return
    }

    resultCountdown.start()
  }

  const handleRating = (rating: number): void => {
    if (satisfactionRating === rating) {
      setSatisfactionRating(null)
      setSessionState({
        userPreferences: {
          satisfactionRating: null,
          satisfactionTimestamp: null
        }
      })
      logActivity('User deselected satisfaction rating')
    } else {
      setSatisfactionRating(rating)
      const timestamp = new Date()
      setSessionState({
        userPreferences: {
          satisfactionRating: rating,
          satisfactionTimestamp: timestamp
        }
      })
      logActivity(`User rated session satisfaction: ${rating} stars`, {
        rating,
        timestamp
      })
    }
  }

  useEffect(() => {
    if (id) {
      const url = resolveGallerySessionUrl(id, config?.settings)
      QRCode.toDataURL(url, {
        margin: 0,
        width: 300
      })
        .then(setQrCodeImage)
        .catch((error) => {
          console.error('[Result] Failed to generate QR code:', error)
        })
    }
  }, [id, config?.settings?.galleryPublicBaseUrl])

  useEffect(() => {
    isMountedRef.current = true

    resultCountdown.setOnTick(() => {
      if (isMountedRef.current) {
        setRemainingTime(resultCountdown.getFormattedRemainingSeconds())
      }
    })

    resultCountdown.setOnComplete(() => {
      if (hasCompleted.current && isMountedRef.current) {
        logActivity(
          `Result page timed out after ${config?.settings?.resultTimeout ?? 120} seconds.`
        )

        const currentState = useSessionStateStore.getState()
        if (currentState.timeline.isCompleted !== true) {
          setSessionState({
            timeline: {
              isCompleted: true
            }
          })
        }

        stateUploader.uploadSessionState(id).catch((err) =>
          console.warn('[RESULT] Background upload on timeout failed:', err)
        )

        if (isMountedRef.current) {
          sessionCleanup()
        }
      }
    })

    const reprintEnabled = config?.settings?.reprintEnabled ?? false
    if (!askUserConsent && !reprintEnabled) {
      resultCountdown.start()
    } else if (!askUserConsent && reprintEnabled) {
      setShowReprint(true)
    }

    const setupResult = async (): Promise<void> => {
      if (hasCompleted.current) return
      hasCompleted.current = true

      let finalStripPreview = ''
      if (gallery?.capturedItems?.length) {
        const stripItem = gallery.capturedItems.find((item) =>
          item.id?.includes('dreambooth-strip')
        )
        finalStripPreview = stripItem?.stripUrl ?? gallery.stripUrl ?? ''
        setStripPreview(finalStripPreview)
      }

      logActivity('Session completed!', {
        stripUrl: finalStripPreview,
        sessionId: id
      })

      const endTime = new Date()
      const duration = timeline?.startTime
        ? endTime.getTime() - new Date(timeline.startTime).getTime()
        : null

      setSessionState({
        timeline: {
          endTime,
          duration,
          isCompleted: true
        }
      })

      await new Promise((resolve) => setTimeout(resolve, 100))

      const currentState = useSessionStateStore.getState()
      if (currentState.timeline.isCompleted !== true) {
        setSessionState({
          timeline: {
            isCompleted: true,
            endTime,
            duration
          }
        })
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      if (!askUserConsent) {
        await stateUploader.uploadSessionState(id)
      } else {
        console.log('[RESULT] Skipping initial upload - waiting for user consent')
      }

      try {
        await stateUploader.triggerSyncNow()
      } catch (syncError) {
        console.warn('[RESULT] Failed to trigger sync:', syncError)
      }
    }

    setupResult()

    return (): void => {
      isMountedRef.current = false
      resultCountdown.stop()
      resultCountdown.setOnComplete(null)
      resultCountdown.setOnTick(null)
    }
  }, [])

  const cachedBackground = useCachedBackgroundImage('/assets/samu/samu-result.webp')
  const cachedLogo = useCachedImage('/assets/samu/samu-logo.png')
  const cachedQrCode = useCachedImage(qrCodeImage)
  const cachedStripUrl = useCachedImage(stripPreview || '')

  const emailEnabled =
    config?.settings?.emailEnabled === undefined || config?.settings?.emailEnabled

  return (
    <>
      {askUserConsent && (
        <ConsentModal
          isVisible={showConsent}
          onConsent={handleConsent}
          stripPreview={stripPreview}
          mode="capture"
        />
      )}

      {config?.settings?.reprintEnabled && (
        <UserReprintModal
          isVisible={showReprint}
          onComplete={() => {
            setShowReprint(false)
            resultCountdown.start()
          }}
          onSkip={() => {
            setShowReprint(false)
            resultCountdown.start()
          }}
          stripPreview={stripPreview}
          timeout={config?.settings?.reprintTimeout ?? 60}
        />
      )}

      {showEmailModal && id && (
        <EmailGalleryModal sessionId={id} onClose={() => setShowEmailModal(false)} />
      )}

      {!showConsent && !showReprint && (
        <div
          className="relative w-full h-full bg-cover bg-center"
          style={{ backgroundImage: `url(${cachedBackground})` }}
        >
          <img
            alt="Samu Logo"
            src={cachedLogo}
            width={200}
            height={200}
            className="absolute top-0 left-6"
          />

          {stripPreview && (
            <img
              className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 object-contain w-[420px]"
              src={cachedStripUrl}
              alt={t('result.photoStripAlt', 'Photo Strip')}
            />
          )}

          <div className="absolute top-[260px] right-[160px] flex flex-col items-center gap-12">
            <div className="w-[350px] h-[350px] p-4 bg-white mr-5 flex items-center justify-center">
              {qrCodeImage ? (
                <img
                  className="w-full h-full"
                  crossOrigin="anonymous"
                  src={cachedQrCode}
                  alt={t('result.qrCodeAlt', 'QR Code')}
                />
              ) : (
                <svg
                  className="w-24 h-24 animate-spin text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
            </div>
            <span className="text-2xl text-carnabyTan font-body-samu text-center mr-4">
              {getGalleryBaseUrl().replace(/^https?:\/\//, '')}
              <br />
              {i18n.language === 'id' ? 'Kode unik : ' : 'Unique code: '}
              {id}
            </span>

            <div className="flex flex-col items-center mt-4">
              <h3 className="text-xl text-carnabyTan font-body-samu font-semibold mb-2">
                {t('result.satisfaction.question')}
              </h3>
              <p className="text-base text-carnabyTan/80 font-body-samu mb-4">
                {t('result.satisfaction.rateExperience')}
              </p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => handleRating(star)}
                    onMouseEnter={() => setHoveredStar(star)}
                    onMouseLeave={() => setHoveredStar(null)}
                    className="transition-all duration-200 hover:scale-110 text-3xl p-2"
                    title={t('result.satisfaction.starAlt', { rating: star })}
                  >
                    <FaStar
                      className={`${
                        (hoveredStar && star <= hoveredStar) ||
                        (satisfactionRating !== null && star <= satisfactionRating)
                          ? 'text-juicyMango'
                          : 'text-carnabyTan/40'
                      } transition-colors duration-200`}
                    />
                  </button>
                ))}
              </div>
              {satisfactionRating !== null && (
                <p className="text-base text-juicyMango font-body-samu font-medium mt-2">
                  {t('result.satisfaction.thankYou')}
                </p>
              )}
            </div>
          </div>

          <div className="absolute bottom-24 right-32 flex flex-col items-center gap-4">
            {(config?.settings?.resultTimeout ?? 0) > 0 && (
              <span className="text-3xl text-carnabyTan font-body-samu">
                {remainingTime}
              </span>
            )}
            <div className="flex flex-row gap-3 items-center">
              <button
                onClick={() => {
                  resultCountdown.stop()

                  const currentState = useSessionStateStore.getState()
                  if (currentState.timeline.isCompleted !== true) {
                    setSessionState({
                      timeline: {
                        isCompleted: true
                      }
                    })
                  }

                  stateUploader.uploadSessionState(id).catch((err) =>
                    console.warn('[RESULT] Background upload failed:', err)
                  )

                  if (isMountedRef.current) {
                    sessionCleanup()
                  }
                }}
                className="rounded-lg bg-carnabyTan text-foundationWhite font-body-samu tracking-widest px-12 py-6 text-4xl hover:brightness-110 transition-opacity"
              >
                {t('result.backToHome', 'Beranda')}
              </button>
              {emailEnabled && (
                <button
                  onClick={() => setShowEmailModal(true)}
                  className="rounded-lg bg-carnabyTan text-foundationWhite font-body-samu tracking-widest px-12 py-6 text-4xl hover:brightness-110 transition-opacity flex items-center gap-2"
                >
                  <IoMail className="text-3xl" />
                  {t('result.emailGallery.sendEmail', 'Send Email')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ResultPage
